'use client';

import Image from 'next/image';
import Link from 'next/link';

import { useDevice } from '@/contexts/DeviceContext';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelConfig';
import { cn } from '@/libs';
import { useCustomerStore, useDrawerStore } from '@/stores';
import { BrandingKey } from '@/types/enums/WhitelabelConfig';

import { useHeader } from './hooks';

import { MenuNavigation } from '../MenuNavigation';
import { Button } from '../ui';
import { useUserDrawer } from '../UserDrawer';

export const Header = () => {
  const { getValue } = useWhitelabelConfig();
  const { isMobile } = useDevice();
  const { isScrolled, isHeaderVisible } = useHeader();
  const openDrawer = useDrawerStore((state) => state.openDrawer);
  const drawers = useDrawerStore((state) => state.drawers);
  const { openUserDrawer } = useUserDrawer();
  const customerData = useCustomerStore((state) => state.customerData);

  const handleOpenMenuDrawer = () =>
    openDrawer({
      title: (
        <Image
          src={getValue(BrandingKey.BRANDING_LOGO_MENU) ?? ''}
          alt={`Logo ${getValue(BrandingKey.BRANDING_NAME) ?? ''}`}
          width={108}
          priority
          height={28}
          className="m-auto"
        />
      ),
      description: null,
      Component: () => <MenuNavigation />,
      drawerContentOptions: {
        className: 'pb-2',
      },
      drawerHeaderOptions: {
        className: 'py-2 bg-footer text-white',
      },
      drawerType: 'menu-navigation',
      drawerOptions: {
        direction: 'left',
      },
    });

  const isMenuNavigationOpen = drawers.some(
    (drawer) => drawer.drawerType === 'menu-navigation',
  );

  const isAuthenticated =
    customerData && customerData.name && customerData.document && customerData.phone;

  const handleUserAccountClick = () => {
    if (isAuthenticated) {
      openUserDrawer();
    } else {
      openUserDrawer();
    }
  };

  return (
    <header
      data-cy="header"
      className={cn(
        'bg-footer fixed w-full top-0 z-50 h-12 transition-all duration-300',
        isScrolled && 'bg-footer',
        isMobile && !isHeaderVisible && '-translate-y-full',
      )}
    >
      <div className="relative flex items-center px-3 h-full shadow-2xl border-b border-foreground/15 w-full">
        <div className="flex flex-row items-center w-full justify-between max-w-screen-xl mx-auto">
          <Button
            data-cy="header-menu-button"
            icon={isMenuNavigationOpen ? 'Close' : 'Hamburger'}
            variant="ghost"
            className="text-background w-9 h-9 [&>svg]:h-6 [&>svg]:w-6"
            onClick={handleOpenMenuDrawer}
          />

          <Link href="/" data-cy="header-logo-link">
            <div className="relative h-10 w-36 sm:h-8 sm:w-48">
              <Image
                src={getValue(BrandingKey.BRANDING_LOGO_HEADER) ?? ''}
                alt={`Logo ${getValue(BrandingKey.BRANDING_NAME) ?? ''}`}
                width={108}
                height={28}
                priority
                className="mx-auto h-full w-full object-contain"
              />
            </div>
          </Link>

          <Button
            data-cy="header-user-button"
            icon="UserAccount"
            variant="ghost"
            className="text-background w-9 h-9 [&>svg]:h-6 [&>svg]:w-6"
            onClick={handleUserAccountClick}
          />
        </div>
      </div>
    </header>
  );
};
